import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  ActionSheetController,
  AlertController,
  LoadingController,
  ToastController,
  ModalController,
} from '@ionic/angular';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { BookingFormDateChangePage } from 'src/app/bookings/booking-form/booking-form-date-change/booking-form-date-change.page';
import { Booking, Service } from '../../../models';
import { BookingService } from '../../../services/booking.service';
import { UserService } from '../../../services/user.service';
import { CalendarService } from '../../../services/calendar.service';
import { Utilities } from '../../../utilities/utilities';
import { List } from 'immutable';
import { Router } from '@angular/router';
import { Storage } from '@ionic/storage-angular';
import { NavigationStateService } from '../../../services/navigation-state.service';
@Component({
  selector: 'smr-booking-form-time-off',
  templateUrl: './booking-form-time-off.page.html',
  styleUrls: ['./booking-form-time-off.page.scss'],
})
export class BookingFormTimeOffPage implements OnInit, OnDestroy {
  public booking: Booking;

  public timeSlots: List<{ label: string; value: number }>;

  public frequencyCountOptions: List<{ name: string; value: number }>;

  public frequencyTypes: List<{ name: string; value: string }>;

  public timeOffForm: UntypedFormGroup;

  public durations: UntypedFormControl;

  public frequencyCount: UntypedFormControl;

  public frequencyType: UntypedFormControl;

  public notes: UntypedFormControl;

  public submitted: boolean;

  public savingInProgress: boolean;

  public deletionInProgress: boolean;

  public navigationStateSubscription;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private bookingService: BookingService,
    private userService: UserService,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private modalCtrl: ModalController,
    private calendarService: CalendarService,
    private actionSheetCtlr: ActionSheetController,
    private router: Router,
    private storage: Storage,
    private navigationStateService: NavigationStateService
  ) {
    this.timeSlots = List([]);

    this.frequencyCountOptions = List.of(
      { name: '1', value: 1 },
      { name: '2', value: 2 },
      { name: '3', value: 3 },
      { name: '4', value: 4 },
      { name: '5', value: 5 },
      { name: '6', value: 6 },
      { name: '7', value: 7 },
      { name: '8', value: 8 },
      { name: '9', value: 9 },
      { name: '10', value: 10 },
      { name: '11', value: 11 },
      { name: '12', value: 12 }
    );

    this.frequencyTypes = List.of(
      { name: 'days', value: 'days' },
      { name: 'weeks', value: 'weeks' },
      { name: 'months', value: 'months' }
    );

    // for (let i = 30; i <= 720; i += 30) {
    for (let i = 15; i <= 720; i += 15) {
      const hour = Math.floor(i / 60);
      const minutes = i % 60;
      const label = `${hour > 0 ? hour + ' hour' : ''}${hour > 1 ? 's' : ''} ${
        minutes > 0 ? minutes + ' mins' : ''
      }`;

      this.timeSlots = this.timeSlots.push({
        label: label,
        value: i,
      });
    }

    this.navigationStateSubscription = this.navigationStateService.getState().subscribe((state) => {
      this.booking = state.booking;
    });
  }

  ngOnInit() {
    this.durations = new UntypedFormControl(
      this.getTimeOffService().getDurations().startDuration,
      Validators.required
    );
    this.notes = new UntypedFormControl(
      this.getTimeOffService().getStylistNotes()
    );
    this.frequencyCount = new UntypedFormControl(
      this.booking.isRecurring()
        ? this.booking.getRecurringSettings().frequencyCount
        : 4
    );
    this.frequencyType = new UntypedFormControl(
      this.booking.isRecurring()
        ? this.booking.getRecurringSettings().frequencyType
        : 'weeks'
    );

    this.timeOffForm = this.formBuilder.group({
      durations: this.durations,
      notes: this.notes,
      frequencyCount: this.frequencyCount,
      frequencyType: this.frequencyType,
    });
    this.submitted = false;
    this.savingInProgress = false;
    this.deletionInProgress = false;
  }

  public ngOnDestroy() {
    this.navigationStateSubscription.unsubscribe();
  }

  public ionViewCanEnter() {

    if (!this.booking) {
      this.router.navigateByUrl('/bookings');
    }
  }

  public async onBookingStartDateTimeTap() {
    let collapsed = await this.storage.get('collapsed');
    const cssClassName = collapsed ? 'modal-fullscreen-c' : 'modal-fullscreen';
    const bookingFormDateChangePage = await this.modalCtrl.create({
      component: BookingFormDateChangePage,
      cssClass: cssClassName,
      componentProps: {
        currentDate: this.booking.startDateTime,
      },
    });
    bookingFormDateChangePage.present();
    bookingFormDateChangePage.onDidDismiss().then((data) => {
      if (data.data) {
        this.booking = this.booking.setStartDateTime(data.data.date);
      }
    });
  }

  public getTimeOffService(): Service {
    return this.booking.getServices().get(0);
  }

  public onRecurringToggleChange($event) {
    if ($event.detail.checked) {
      this.booking = this.booking.setRecurringFrequencyCount(4);
      this.booking = this.booking.setRecurringFrequencyType('weeks');
      this.frequencyCount.setValue(4);
      this.frequencyType.setValue('weeks');
    } else {
      this.booking = this.booking.turnOffRecurring();
    }
  }

  public onRecurringFrequencyCountChange() {
    this.booking = this.booking.setRecurringFrequencyCount(this.frequencyCount.value);
  }

  public onRecurringFrequencyTypeChange() {
    this.booking = this.booking.setRecurringFrequencyType(this.frequencyType.value);
  }

  public async onTimeOffSave() {
    this.submitted = true;

    if (!this.timeOffForm.valid) {
      return;
    }

    if (
      this.booking.isAlreadyLinkedToARecurringAppointment() &&
      this.booking.recurring.change !== 'turnoff'
    ) {
      const actionSheet = await this.actionSheetCtlr.create({
        header:
          'You are changing a recurring blocked time. Do you want to change all future blocked times as well?',
        buttons: [
          {
            text: 'Yes, Change all future blocked times',
            handler: () => {
              actionSheet.dismiss().then(() => {
                if (this.booking.isRecurring()) {
                  this.booking =
                    this.booking.setRecurringChangeOptionType('future');
                  this.save();
                }
              });

              return false;
            },
          },
          {
            text: 'No, Just change this blocked time and keep all future ones as they are',
            handler: () => {
              actionSheet.dismiss().then(() => {
                this.booking =
                  this.booking.setRecurringChangeOptionType('single');
                this.save();
              });

              return false;
            },
          },
          {
            text: 'Cancel',
            role: 'cancel',
            handler: () => {
              actionSheet.dismiss();
              return false;
            },
          },
        ],
      });

      actionSheet.present();
    } else {
      this.save();
    }
  }

  private save() {
    this.booking = this.booking.setServices(
      List.of(
        this.getTimeOffService()
          .setDurations({
            startDuration: parseInt(this.durations.value, 10),
            processDuration: 0,
            finishDuration: 0,
          })
          .setStylistNotes(this.notes.value)
      )
    );

    // perform saving
    this.savingInProgress = true;
    this.bookingService.saveBooking(this.booking).subscribe({
      next: async (booking) => {
        this.router.navigateByUrl('/bookings');

        this.savingInProgress = false;
        this.calendarService.makeCalendarOutdated();
        const toast = await this.toastCtrl.create({
          message: `Time off has been ${
            this.booking.getId() === undefined ? 'created' : 'updated'
          }`,
          duration: 3000,
        });
        toast.present();
      },
      error: async (err) => {
        this.savingInProgress = false;
        const alert = await this.alertCtrl.create(
          Utilities.getDefaultErrorMessageConfig()
        );
        alert.present();
      },
    });
  }

  public async onTimeOffDelete() {
    // this.deletionInProgress = true;
    // this.bookingService.deleteBooking(this.booking)
    //   .subscribe(
    //       booking => {
    //         this.navCtrl.popToRoot();
    //         this.deletionInProgress = false;
    //         this.calendarService.makeCalendarOutdated();
    //         this.toastCtrl.create({message: 'Time off has been deleted', duration: 3000}).present();
    //       },
    //       err => {
    //         this.deletionInProgress = false;
    //         this.alertCtrl.create(Utilities.getDefaultErrorMessageConfig()).present();
    //       }
    //     );

    if (this.booking.isAlreadyLinkedToARecurringAppointment()) {
      const actionSheet = await this.actionSheetCtlr.create({
        header:
          'You are deleting a recurring service. Do you want to delete all future services as well?',
        buttons: [
          {
            text: 'Yes, Delete all future services',
            handler: () => {
              actionSheet.dismiss().then(() => {
                this.finalizeBookingDeletion('future');
              });

              return false;
            },
          },
          {
            text: 'No, Just delete this service and keep all future ones as they are',
            handler: () => {
              actionSheet.dismiss().then(() => {
                this.finalizeBookingDeletion('single');
              });

              return false;
            },
          },
          {
            text: 'Cancel',
            role: 'cancel',
            handler: () => {
              actionSheet.dismiss();
              return false;
            },
          },
        ],
      });

      actionSheet.present();
    } else {
      // normal booking deletion
      this.initiateBookingDeletetion('single');
    }
  }

  private async finalizeBookingDeletion(action: string) {
    const loader = await this.loadingCtrl.create({
      spinner: 'crescent',
    });

    loader.present();

    this.bookingService.deleteBooking(this.booking, action).subscribe({
      next: (result) => {
        loader.dismiss().then(() => {
          this.calendarService.makeCalendarOutdated();
          this.router.navigateByUrl('/bookings').then(async () => {
            const toast = await this.toastCtrl.create({
              message: 'Booking has been deleted',
              duration: 3000,
            });
            toast.present();
          });
        });
      },
      error: (error) => {
        loader.dismiss().then(async () => {
          const alert = await this.alertCtrl.create(
            Utilities.getDefaultErrorMessageConfig()
          );
          alert.present();
        });
      },
    });
  }

  private async initiateBookingDeletetion(action: string) {
    // const loader = this.loadingCtrl.create({
    //   content: 'Deleting...',
    //   spinner: 'crescent'
    // });

    // loader.present();

    const actionSheet = await this.actionSheetCtlr.create({
      header: 'Are you sure you want to delete this booking?',
      buttons: [
        // {
        //   text: `Delete this booking and email the client`,
        //   icon: 'trash',
        //   role: 'destructive',
        //   cssClass: 'sm-error',
        //   handler: () => {

        //     actionSheet.dismiss().then(() => {
        //       this.booking = this.booking.setSendEmail(1);
        //       this.finalizeBookingDeletion(action);
        //     });

        //     return false;
        //   }
        // },
        {
          text: `Yes, delete this booking.`,
          icon: 'trash',
          role: 'destructive',
          cssClass: 'sm-error',
          handler: () => {
            actionSheet.dismiss().then(() => {
              this.booking = this.booking.setSendEmail(0);
              this.finalizeBookingDeletion(action);
            });

            return false;
          },
        },
        {
          text: 'Cancel',
          role: 'cancel',
          icon: 'close',
          handler: () => {
            actionSheet.dismiss();
            return false;
          },
        },
      ],
    });

    actionSheet.present();
  }
}
